/**
 * Web-side RBAC helpers for the Infrastructure (Docker control plane) area.
 *
 * THIN ADAPTER over the single shared tier helper in `auth/tier.ts`. The whole
 * three-tier model (viewer / operator / admin) and the API-aligned
 * `currentUser.tier` resolution live there; this file only re-labels the two
 * gates the infrastructure stores/components read so their call sites stay
 * stable:
 *   - canManageInfra → operator OR admin (start/stop/restart/deploy/rollback)
 *   - isInfraAdmin   → admin only (exec + host/secret config)
 *
 * SECURITY: a green light here is never sufficient — the Portainer admin key is
 * root-equivalent and exec is admin-only, so the API re-checks the effective
 * tier on every mutating call and every stream subscribe regardless of what the
 * UI shows.
 */
import type RootStore from "stores/RootStore";
import { isOperator, isAdmin } from "auth/tier";

/**
 * operator OR admin — may start/stop/restart/deploy/rollback. Mirrors the
 * devops `canManage` getter so the two areas gate identically.
 */
export function canManageInfra(rootStore: RootStore): boolean {
  return isOperator(rootStore);
}

/**
 * admin ONLY — may open an exec session and configure hosts/secrets. Exec is the
 * highest-privilege action (a shell inside a client's container), so it is gated
 * strictly on the admin tier, never operator.
 */
export function isInfraAdmin(rootStore: RootStore): boolean {
  return isAdmin(rootStore);
}
